import {
  Box,
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CustomTable from "global/components/CustomTable/CustomTable";
import strings from "global/constants/StringConstants";
import {
  debounceEventHandler,
  getFormattedStatsCount,
  handleErrorMessage,
  initialPageFilterState,
  isTruthy,
  openSuccessNotification,
  validatePageQuery,
  validatePerPageQuery,
} from "helpers/methods";
import { useEffect, useRef, useState } from "react";
import SearchIcon from "assets/icons1/search_icon.svg";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import {
  fetchRoles,
  resetInActiveUserLimit,
  resetMFA,
  unlockUser,
} from "../SettingsService";
import { pageFilters, UserFields } from "models/interfaces";
import InviteUser from "./InviteUser";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import userStyles from "./Users.styles";
import SendInviteIcon from "assets/icons1/SendInviteIcon.svg";
import {
  getCampaigners,
  getCampaignersCount,
  updateCampaignerStatus,
} from "./UsersService";
import MoreActionIcon from "assets/icons1/MoreActionIcon.svg";
import Reset2FAIcon from "assets/icons1/Rest2FAIcon.svg";
import UnLockUserIcon from "assets/icons1/UnlockUserIcon.svg";
import ActivateUserIcon from "assets/icons1/ActivateUserIcon.svg";
import Deactivate_Icon from "assets/icons1/Deactivate_Icon.svg";
import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";
import LockedUser from "assets/icons1/LockedUser.svg";
import { useLocation } from "react-router-dom";
import EditIcon from "assets/icons1/EditIcon.svg";

const tableHeader = [
  {
    name: "User Email",
    field: "email",
    align: "left",
  },
  {
    name: "Manager's Email",
    field: "managedBy",
    align: "left",
  },
  {
    name: "Email Count",
    field: "allowedEmailCount",
    align: "left",
  },
  {
    name: "Used Email",
    field: "emailCount",
    align: "left",
  },
  {
    name: "Role",
    field: "role",
    align: "left",
  },
  {
    name: "Status",
    field: "status",
    align: "left",
  },
  {
    name: "Action",
    field: "action",
    align: "left",
    hide: true,
  },
];

const Users = () => {
  useTitle(title.USERS);
  const theme = useTheme();
  const classes = userStyles(theme);
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
  const [count, setCount] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isMFAEnabled, setIsMFAEnabled] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<any>();
  const [selectedUserRoleId, setSelectedUserRoleId] = useState<any>();
  const [inviteUser, setInviteUser] = useState<{
    visible: boolean;
    metadata: UserFields;
  }>({
    visible: false,
    metadata: {
      email: "",
      name: "",
      firstName: "",
      lastName: "",
      managedBy: "",
      managerId: 0,
      roleIds: [],
      allowedEmailCount: "",
    },
  });
  const searchTerm = useRef<any>(null);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const searchFromUrl = urlParams.get("search");

  const [usersFilters, setUsersFilters] = useState<pageFilters>(
    initialPageFilterState(pageFromUrl, rowsPerPageFromUrl, searchFromUrl ?? "")
  );

  const userActionMenu = (selectedUserStatus: string) => {
    const isUserLocked = selectedUserStatus === "Locked";
    const isUserActive = selectedUserStatus === "Active";
    const menuItems = [
      {
        label: "Edit",
        value: "edit",
        icon: <img src={EditIcon} />,
      },
      {
        label: "Unlock User",
        value: "unlock",
        icon: <img src={isUserLocked ? LockedUser : UnLockUserIcon} />,
        disabled: !isUserLocked,
      },
      {
        label: "Reset 2FA",
        value: "reset2FA",
        icon: <img src={Reset2FAIcon} />,
        disabled: !isMFAEnabled,
      },
      {
        label: isUserActive ? "Deactivate User" : "Activate User",
        value: "activate",
        icon: <img src={isUserActive ? Deactivate_Icon : ActivateUserIcon} />,
      },
    ];

    return isUserLocked
      ? menuItems.filter((item) => item.value !== "activate")
      : menuItems;
  };

  useEffect(() => {
    const { page, rowsPerPage, searchText } = usersFilters;
    if (searchTerm.current !== null) searchTerm.current.value = searchText;
    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}`
    );
    getUserData();
  }, [usersFilters]);

  const searchChangeHandler = (event: any) => {
    const searchText = event.target.value;
    setUsersFilters({
      ...usersFilters,
      page: 1,
      searchText,
    });
  };

  const pageChangeHandler = (event: any, newPage: number) => {
    setUsersFilters({
      ...usersFilters,
      page: newPage,
    });
  };

  const perPageRowsChangeHandler = (rowPerPage: number) => {
    setUsersFilters({
      ...usersFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };

  const handleOpenMenu = (event: any, user: any) => {
    setIsMFAEnabled(user.multiFactorLogin);
    setAnchorEl(event.currentTarget);
  };

  const getUserData = async () => {
    const { searchText, page, rowsPerPage } = usersFilters;
    try {
      setLoading(true);
      const [roles, count, response] = await Promise.all([
        fetchRoles("All", 0),
        getCampaignersCount(searchText),
        getCampaigners(page, rowsPerPage, searchText, []),
      ]);
      const tableData = convertDataToTableFormat(response, roles);
      setCount(count);
      setRoles(
        roles.map((role: any) => {
          return {
            id: role.id,
            name: role.name,
          };
        })
      );
      setUsers([...tableData]);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetMFA = async (email: string) => {
    try {
      await resetMFA(email);
      openSuccessNotification(`MFA has been reset for ${email}.`);
      await getUserData();
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleUnlockUser = async (email: string) => {
    try {
      await unlockUser(email);
      openSuccessNotification(
        `Account associated with ${email} has been unlocked successfully.`
      );
      await getUserData();
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const handlerUpdateCampaignerStatus = async () => {
    try {
      setLoading(true);
      const res = await updateCampaignerStatus(
        selectedUser?.userId,
        selectedUser?.status === "Active" ? "Inactive" : "Active"
      );

      if (res && selectedUser?.status === "Active") {
        try {
          await resetInActiveUserLimit(selectedUser.userId);
        } catch (error: any) {
          handleErrorMessage(error);
        }
      }
      openSuccessNotification("Campaigner status updated!");
      await getUserData();
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const convertDataToTableFormat = (
    data: any,
    allRoles: { id: number; name: string }[] = []
  ) => {
    return data.map((user: any) => {
      allRoles = isTruthy(allRoles) ? allRoles : roles;
      const roleId = allRoles.find((role) => role.name === user.role)?.id;
      const userStatus = user.status;
      return {
        email: (
          <Link
            color="text.primary"
            variant="subtitle2"
            component={"button"}
            onClick={() =>
              history.push({
                pathname: `${viewpaths.viewUserViewPath}/${user.userId}`,
                state: {
                  userData: user,
                  roleIds: roleId,
                },
              })
            }
          >
            {user.email}
          </Link>
        ),
        managedBy: isTruthy(user.managedBy) ? user.managedBy : "-",
        allowedEmailCount: getFormattedStatsCount(user.allowedEmailCount ?? 0),
        emailCount: getFormattedStatsCount(user.emailCount ?? 0),
        role: user.role,
        status: (
          <Typography
            sx={{ color: userStatus === "Active" ? "#72C20B" : "#FF9436" }}
            variant="subtitle2"
          >
            {userStatus === "Inactive" ? "In Active" : userStatus}
          </Typography>
        ),
        action: (
          <IconButton
            size="small"
            sx={{
              color: "#fff",
              padding: 0,
              cursor: "pointer",
            }}
            onClick={(e: any) => {
              e.stopPropagation();
              setSelectedUser(user);
              handleOpenMenu(e, user);
              setSelectedUserRoleId(roleId);
            }}
          >
            <img src={MoreActionIcon} width="4px" />
          </IconButton>
        ),
      };
    });
  };

  const getPageHeader = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Stack>
          <TextField
            id="users_search_box"
            placeholder="Search users"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.MuiOutlinedInput-root.Mui-focused": {
                border: "none !important",
              },
            }}
            fullWidth
            onChange={debounceEventHandler(
              searchChangeHandler,
              strings.SEARCH_TIME_OUT
            )}
            inputRef={searchTerm}
            InputProps={{
              startAdornment: (
                <Stack mr={1}>
                  <img src={SearchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
          />
        </Stack>
      </CenteredCardLayout>
    );
  };

  const getInviteUser = () => {
    return (
      <InviteUser
        user={inviteUser.metadata}
        allRoles={roles}
        visible={inviteUser.visible}
        isEdit={isEdit}
        handleDialogClose={() =>
          setInviteUser({
            visible: false,
            metadata: {
              email: "",
              name: "",
              firstName: "",
              lastName: "",
              allowedEmailCount: "",
              managedBy: "",
              managerId: 0,
              roleIds: [],
            },
          })
        }
        handleGetUsers={getUserData}
      />
    );
  };

  const getUsersPage = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h5" color="text.primary">
            Users
          </Typography>
          <Box>
            <Button
              id="send_invite"
              variant="contained"
              size="small"
              onClick={() => {
                setIsEdit(false);
                setInviteUser({
                  ...inviteUser,
                  visible: true,
                });
              }}
              disabled={loading}
              startIcon={<img src={SendInviteIcon} />}
            >
              Send Invite
            </Button>
          </Box>
        </Stack>

        {getPageHeader()}
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={tableHeader}
              rows={users}
              page={usersFilters.page}
              rowsPerPage={usersFilters.rowsPerPage}
              onRowsPerPageChange={perPageRowsChangeHandler}
              onPageChange={pageChangeHandler}
              count={count}
              showJumpToPageNumber={false}
              handleOnChangePageInput={pageChangeHandler}
              showPagination={true}
              isLoading={loading}
            />
          </Box>
        </CenteredCardLayout>
        {getInviteUser()}
      </Stack>
    );
  };

  const handleMenuItemClick = async (action: string) => {
    switch (action) {
      case "edit":
        setAnchorEl(null);
        history.push({
          pathname: `${viewpaths.viewUserViewPath}/${selectedUser.userId}`,
          state: {
            userData: selectedUser,
            roleIds: selectedUserRoleId,
          },
        });
        break;
      case "unlock":
        setAnchorEl(null);
        await handleUnlockUser(selectedUser.email);
        break;
      case "reset2FA":
        setAnchorEl(null);
        await handleResetMFA(selectedUser.email);
        break;
      case "activate":
        setAnchorEl(null);
        await handlerUpdateCampaignerStatus();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <CenteredAuthenticatedLayout>
        {getUsersPage()}
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => {
            setAnchorEl(null);
            setSelectedUser(null);
          }}
        >
          {selectedUser &&
            userActionMenu(selectedUser.status).map(
              (item: any, index: number) => (
                <MenuItem
                  key={index}
                  value={item.value}
                  sx={classes.dropDownMenuItem}
                  disabled={item.disabled}
                  onClick={() => handleMenuItemClick(item.value)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {item.icon}
                    <Typography variant="body2" component="span">
                      {item.label}
                    </Typography>
                  </Box>
                </MenuItem>
              )
            )}
        </Menu>
      </CenteredAuthenticatedLayout>
    </>
  );
};

export default Users;
